import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { listInterviews, getCategories } from "../lib/api";
import InterviewCard from "../components/InterviewCard";

const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="h-px w-10 bg-medblue" />
    <span className="text-[10px] uppercase tracking-[0.28em] text-medblue">{children}</span>
  </div>
);

export default function Interviews() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (active !== "All") params.category = active;
    if (q) params.q = q;
    const t = setTimeout(() => {
      listInterviews(params).then((data) => { setItems(data); setLoading(false); }).catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [active, q]);

  const tabs = useMemo(() => ["All", ...categories], [categories]);

  return (
    <div className="bg-white" data-testid="interviews-page">
      <section className="border-b border-rule">
        <div className="container-wide py-20 lg:py-24 max-w-5xl">
          <Eyebrow>The Archive</Eyebrow>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-[68px] leading-[1.03] tracking-tight text-navy">
            Interviews with tomorrow&rsquo;s clinicians, researchers &amp; founders.
          </h1>
          <p className="mt-8 text-lg text-navy/65 max-w-2xl leading-relaxed">
            A living library of conversations across medicine — searchable by specialty, organization, or field.
          </p>
        </div>
      </section>

      <section className="container-wide py-10">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10 border-b border-rule pb-8">
          <div className="relative flex-1 max-w-xl">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, specialty, organization…"
              className="w-full border border-rule focus:border-navy outline-none pl-10 pr-4 py-3 text-sm bg-white"
              data-testid="search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="category-tabs">
            {tabs.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[11px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${
                  active === c
                    ? "bg-navy text-white border-navy"
                    : "bg-white text-navy/70 border-rule hover:border-navy hover:text-navy"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="border border-rule">
                  <div className="aspect-[4/5] bg-parchment animate-pulse" />
                  <div className="p-6 space-y-3">
                    <div className="h-3 w-20 bg-parchment animate-pulse" />
                    <div className="h-6 w-3/4 bg-parchment animate-pulse" />
                    <div className="h-3 w-1/2 bg-parchment animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="border border-dashed border-navy/25 py-24 lg:py-28 text-center" data-testid="empty-state">
              <div className="font-serif text-3xl md:text-4xl text-navy/35 italic">
                {q || active !== "All" ? "Nothing matches — yet." : "Interviews coming soon."}
              </div>
              <p className="mt-5 text-navy/60 max-w-md mx-auto">
                {q || active !== "All"
                  ? "Try a different search or category."
                  : "We're currently scheduling our first conversations with healthcare professionals. Check back soon."}
              </p>
              {!(q || active !== "All") && (
                <Link to="/join" className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-medblue hover:text-navy">
                  Want to be featured? Get in touch <ArrowRight size={13} />
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="interview-grid">
              {items.map((iv, idx) => <InterviewCard key={iv.id} interview={iv} index={idx} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
